import { zodResolver } from '@hookform/resolvers/zod';
import type { SalePaymentPart } from '@repo/desktop-bridge';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { useForm } from 'react-hook-form';

import { formatPaise, rupeesFromPaise } from '@/lib/money';

import { fieldLabel, fieldSurface, FormDialogContent } from '../auth/AuthFrame';
import { PAYMENT_MODES } from './payment-modes';
import {
  partsFromSplitForm,
  REFERENCE_FIELD,
  splitFormSchema,
  type SplitFormValues,
  splitLabel,
  splitTotal,
} from './split-payment';

/**
 * One bill paid two or three ways (#357): an amount for each mode the guest is
 * paying by, and the reference for a UPI or card part.
 *
 * **Held to the bill here, before anything is recorded**: at least two parts,
 * each above nothing, adding up to `totalPaise` exactly — the total after any
 * discount and the round off, which is what the API holds the parts to. The
 * main process checks it again before a number is spent. Left to take is said
 * as the cashier types, and *The rest* fills a mode with it, so nobody does the
 * subtraction in their head.
 */
export function SplitPaymentDialog({
  current,
  totalPaise,
  onApply,
  onRemove,
  onClose,
}: {
  current: readonly SalePaymentPart[] | null;
  /** What the parts must add up to. */
  totalPaise: number;
  onApply: (parts: SalePaymentPart[]) => void;
  onRemove: () => void;
  onClose: () => void;
}) {
  const schema = splitFormSchema(totalPaise);
  const typed = (mode: SalePaymentPart['paymentMode']) => {
    const part = current?.find((each) => each.paymentMode === mode);

    return { amount: part === undefined ? '' : rupeesFromPaise(part.amountPaise), part };
  };
  const form = useForm<SplitFormValues>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: {
      CASH: typed('CASH').amount,
      UPI: typed('UPI').amount,
      CARD: typed('CARD').amount,
      UPI_REFERENCE: typed('UPI').part?.paymentReference ?? '',
      CARD_REFERENCE: typed('CARD').part?.paymentReference ?? '',
    },
  });

  // What the fields say so far, against the bill — a malformed amount counts
  // as nothing until it is one.
  const sumPaise = partsFromSplitForm(form.watch()).reduce(
    (sum, part) => sum + part.amountPaise,
    0,
  );
  const leftPaise = totalPaise - sumPaise;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <FormDialogContent>
        <DialogHeader>
          <DialogTitle>Split payment</DialogTitle>
          <DialogDescription className="numeric">
            {formatPaise(totalPaise)} across two or three ways to pay.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            className="space-y-5"
            noValidate
            onSubmit={(event) => {
              // A portal is still inside the sale's React tree, and a submit
              // that bubbled out of here would take payment.
              event.stopPropagation();
              void form.handleSubmit((values) => {
                onApply(partsFromSplitForm(values));
              })(event);
            }}
          >
            {PAYMENT_MODES.map(({ mode, label }) => (
              <div key={mode} className="space-y-3">
                <FormField
                  control={form.control}
                  name={mode}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={fieldLabel}>{label}, ₹</FormLabel>
                      <div className="flex items-center gap-2">
                        <FormControl
                          render={
                            <Input
                              className={`${fieldSurface} h-touch min-w-0 flex-1 font-mono text-lg numeric md:text-lg`}
                              inputMode="decimal"
                              placeholder="500.00"
                              autoComplete="off"
                              {...field}
                            />
                          }
                        />
                        {/* Fills this mode with what the others leave, so a
                            cash-then-UPI bill is one amount typed and one tap. */}
                        <Button
                          type="button"
                          variant="outline"
                          size="touch"
                          className="shrink-0 px-4"
                          disabled={leftPaise + amountOf(field.value) <= 0}
                          onClick={() => {
                            field.onChange(rupeesFromPaise(leftPaise + amountOf(field.value)));
                          }}
                        >
                          The rest
                        </Button>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* A reference only against a UPI or card part that took
                    something — cash never carries one. */}
                {mode !== 'CASH' && form.watch(mode).trim() !== '' && (
                  <FormField
                    control={form.control}
                    name={REFERENCE_FIELD[mode]}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={fieldLabel}>{label} reference (optional)</FormLabel>
                        <FormControl
                          render={
                            <Input
                              className={`${fieldSurface} h-touch font-mono text-lg numeric md:text-lg`}
                              placeholder="431109882314"
                              autoComplete="off"
                              inputMode="numeric"
                              {...field}
                            />
                          }
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
            ))}

            <p className="flex items-baseline justify-between text-sm text-muted-foreground">
              <span>{leftPaise < 0 ? 'Over the bill by' : 'Left to take'}</span>
              <span className="font-mono text-base text-foreground numeric">
                {formatPaise(Math.abs(leftPaise))}
              </span>
            </p>

            {/* The rules about the whole split, above the submit. */}
            <FormField
              control={form.control}
              name="split"
              render={() => (
                <FormItem>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              {current !== null && (
                <Button type="button" variant="outline" size="touch" onClick={onRemove}>
                  Remove split
                </Button>
              )}
              <Button type="submit" size="touch">
                Apply split
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </FormDialogContent>
    </Dialog>
  );
}

/** A typed amount in paise, or nothing while it is not one. */
function amountOf(typed: string): number {
  return partsFromSplitForm({ CASH: typed }).reduce((sum, part) => sum + part.amountPaise, 0);
}

/**
 * The split on the bill, above the total, as the discount sits there — and in
 * the refusal colour, saying by how much, once the basket has moved under it.
 * Take payment refuses it then; it is not quietly cut to fit.
 */
/**
 * **Split, as the fourth answer to How they paid** (#357) — beside Cash, UPI
 * and Card, in the one radio group, because a split *is* how they paid. A press
 * opens the dialog; it reads as chosen once a split is applied, and a mode
 * tapped afterwards replaces it. A bill paid one way is still one tap.
 *
 * In the payment row rather than beside Take payment because the sale column
 * has no room: at 1000x625 a third press there left Take payment 97px for a
 * label that needs 106.
 */
export function SplitAnswer({
  split,
  disabled,
  onSplit,
  className = 'px-4',
}: {
  split: boolean;
  disabled: boolean;
  onSplit: (() => void) | null;
  className?: string;
}) {
  return (
    <Button
      type="button"
      role="radio"
      aria-checked={split}
      aria-label="Split payment"
      variant={split ? 'secondary' : 'outline'}
      size="touch"
      className={className}
      disabled={disabled}
      onClick={() => onSplit?.()}
    >
      Split
    </Button>
  );
}

export function SplitSummary({
  payments,
  totalPaise,
}: {
  payments: readonly SalePaymentPart[];
  totalPaise: number;
}) {
  const sum = splitTotal(payments);
  const fits = sum === totalPaise;

  return (
    <div
      className={`flex flex-wrap items-baseline gap-x-4 text-sm ${fits ? 'text-muted-foreground' : 'text-destructive'}`}
    >
      {/* "Split" alone, so a two-part split stays on one line of a 22rem column. */}
      <span>Split</span>
      <span className="ms-auto font-mono numeric">
        {fits
          ? splitLabel(payments)
          : `${formatPaise(sum)} — the bill is ${formatPaise(totalPaise)}`}
      </span>
    </div>
  );
}
